import { forwardRef, HTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

// Card Variants
export type CardVariant = 'default' | 'interactive' | 'outlined' | 'ghost';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => {
    const variantStyles = {
      default: [
        'bg-[var(--color-surface-primary)]',
        'border border-[var(--color-border-default)]',
        'shadow-[var(--shadow-md)]',
        'rounded-[var(--radius-md)]',
        'transition-all duration-[var(--duration-fast)] ease-[var(--easing-default)]',
      ],
      interactive: [
        'bg-[var(--color-surface-primary)]',
        'border border-[var(--color-border-default)]',
        'shadow-[var(--shadow-md)]',
        'rounded-[var(--radius-md)]',
        'cursor-pointer',
        'transition-all duration-[var(--duration-fast)] ease-[var(--easing-default)]',
        'hover:shadow-[var(--shadow-lg)]',
        'hover:-translate-y-0.5',
        'active:scale-[0.99]',
      ],
      outlined: [
        'bg-transparent',
        'border border-[var(--color-border-default)]',
        'rounded-[var(--radius-md)]',
        'shadow-none',
      ],
      ghost: [
        'bg-transparent',
        'border-0',
        'rounded-[var(--radius-md)]',
        'shadow-none',
      ],
    };

    return (
      <div
        ref={ref}
        className={cn(variantStyles[variant], className)}
        {...props}
      />
    );
  }
);
Card.displayName = 'Card';

const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col',
        'p-[var(--spacing-lg)]',
        'space-y-[var(--spacing-sm)]',
        className
      )}
      {...props}
    />
  )
);
CardHeader.displayName = 'CardHeader';

const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn(
        'text-[var(--font-size-lg)]',
        'font-[var(--font-weight-semibold)]',
        'text-[var(--color-text-primary)]',
        'leading-[var(--line-height-tight)]',
        'tracking-[var(--letter-spacing-tight)]',
        className
      )}
      {...props}
    />
  )
);
CardTitle.displayName = 'CardTitle';

const CardDescription = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        'text-[var(--font-size-sm)]',
        'text-[var(--color-text-secondary)]',
        'mt-[var(--spacing-xs)]',
        className
      )}
      {...props}
    />
  )
);
CardDescription.displayName = 'CardDescription';

const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'p-[var(--spacing-lg)]',
        'pt-0',
        className
      )}
      {...props}
    />
  )
);
CardContent.displayName = 'CardContent';

const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center',
        'p-[var(--spacing-lg)]',
        'pt-[var(--spacing-md)]',
        'border-t border-[var(--color-border-default)]',
        'bg-[var(--color-surface-secondary)]',
        'rounded-b-[var(--radius-md)]',
        className
      )}
      {...props}
    />
  )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
